import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import * as XLSX from "xlsx";
import CommonTable from "../components/CommonTable";

const formatNumberCH = (value, options = {}) => {
    if (value === null || value === undefined || value === "") {
        return "0";
    }
    const numberValue = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numberValue)) {
        return value?.toString ? value.toString() : "";
    }
    const {
        minimumFractionDigits = 0,
        maximumFractionDigits = Math.max(minimumFractionDigits, 2),
    } = options;

    return numberValue.toLocaleString("de-CH", {
        minimumFractionDigits,
        maximumFractionDigits,
    });
};

const Calculations = () => {
    const [calculations, setCalculations] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Filter states
    const [availableBuildings, setAvailableBuildings] = useState([]);
    const [availablePeriods, setAvailablePeriods] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [filteredCalculations, setFilteredCalculations] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [allCalculations, setAllCalculations] = useState([]);

    useEffect(() => {
        fetchAvailableOptions();
        fetchAllCalculations(); // Load all calculations by default
    }, []);

    // Fetch available buildings for filters
    const fetchAvailableOptions = async () => {
        try {
            // Get buildings from building API
            const buildingsRes = await axios.get(`${process.env.REACT_APP_API_URL}building/list`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                }
            });

            if (buildingsRes.data.status === "success") {
                const buildings = buildingsRes.data.data.buildings || [];
                const buildingOptions = buildings.map(building => ({
                    value: building.id,
                    label: building.building_name
                }));
                
                setAvailableBuildings(buildingOptions);
            }
        } catch (err) {
            console.error("Error fetching available options:", err);
        }
    };

    // Fetch all calculations (default view)
    const fetchAllCalculations = async () => {
        try {
            setLoading(true);
            
            const res = await axios.get(`${process.env.REACT_APP_API_URL}calculations`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                params: {
                    limit: 1000
                }
            });

            if (res.data.status === "success") {
                const calculationsData = res.data.data.calculations || [];
                
                // Filter out calculations with 0 results, but keep those with no_cost_destination flag
                const filteredCalculationsData = calculationsData.filter(calc => {
                    const withoutVat = parseFloat(calc.result_without_vat || 0);
                    const withVat = parseFloat(calc.result_with_vat || 0);
                    // Include calculations with no_cost_destination even if they're 0
                    if (calc.no_cost_destination) {
                        return true;
                    }
                    return withoutVat !== 0 || withVat !== 0;
                });
                
                // Format data for CommonTable
                const formattedCalculations = filteredCalculationsData.map((calc) => ({
                    id: calc.id,
                    "Key": calc.key_name || `Key ${calc.key_id}`,
                    "Type": calc.calculation_type,
                    "Cost": formatCostColumn(calc),
                    "Surface": formatSurfaceColumn(calc),
                    "Consumption": formatConsumptionColumn(calc),
                    "Variables": formatRemainingVariables(calc.variables),
                    "Result Without VAT": (calc.result_without_vat !== null && calc.result_without_vat !== undefined) || calc.no_cost_destination
                        ? `${formatNumberCH(calc.result_without_vat || 0, { minimumFractionDigits: 0, maximumFractionDigits: 4 })}${calc.result_unit ? ' ' + calc.result_unit : ''}`
                        : '-',
                    "Result With VAT": (calc.result_with_vat !== null && calc.result_with_vat !== undefined) || calc.no_cost_destination
                        ? `${formatNumberCH(calc.result_with_vat || 0, { minimumFractionDigits: 0, maximumFractionDigits: 4 })}${calc.result_unit ? ' ' + calc.result_unit : ''}`
                        : '-',
                    "Building": calc.building_name || 'Unknown Building',
                    "Destination Type": calc.destination_type || '-',
                    "Destination": formatDestinationColumn(calc),
                    // Store raw data for export
                    rawData: calc
                }));

                setCalculations(formattedCalculations);
                setAllCalculations(filteredCalculationsData); // Store raw data
            } else {
                setCalculations([]);
            }
        } catch (err) {
            console.error("Error fetching all calculations:", err);
            setCalculations([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle building selection and load periods
    const handleBuildingChange = async (buildingOption) => {
        setSelectedBuilding(buildingOption);
        setSelectedPeriod(null);
        
        if (buildingOption) {
            try {
                // Fetch periods for the selected building
                const res = await axios.get(`${process.env.REACT_APP_API_URL}building/${buildingOption.value}/periods`, {
                    headers: {
                        "api-key": process.env.REACT_APP_API_KEY,
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    }
                });

                if (res.data.status === "success") {
                    const periods = res.data.data.periods || [];
                    const periodOptions = periods.map(period => ({
                        value: period.id,
                        label: `${new Date(period.start_date).toLocaleDateString()} - ${new Date(period.end_date).toLocaleDateString()}`
                    }));
                    setAvailablePeriods(periodOptions);
                } else {
                    setAvailablePeriods([]);
                }
            } catch (err) {
                console.error("Error fetching periods:", err);
                setAvailablePeriods([]);
            }
        } else {
            setAvailablePeriods([]);
        }
    };

    // Fetch filtered calculations
    const fetchFilteredCalculations = async () => {
        if (!selectedBuilding) {
            alert("Please select a building");
            return;
        }

        try {
            setLoading(true);
            setHasSearched(true);
            
            const params = {
                building_id: selectedBuilding.value,
                limit: 100000
            };
            
            if (selectedPeriod) {
                params.period_id = selectedPeriod.value;
            }
            
            const res = await axios.get(`${process.env.REACT_APP_API_URL}calculations`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                params
            });

            if (res.data.status === "success") {
                const calculationsData = res.data.data.calculations || [];
                
                // Filter out calculations with 0 results, but keep those with no_cost_destination flag
                const filteredCalculationsData = calculationsData.filter(calc => {
                    const withoutVat = parseFloat(calc.result_without_vat || 0);
                    const withVat = parseFloat(calc.result_with_vat || 0);
                    // Include calculations with no_cost_destination even if they're 0
                    if (calc.no_cost_destination) {
                        return true;
                    }
                    return withoutVat !== 0 || withVat !== 0;
                });
                
                // Format data for CommonTable
                const formattedCalculations = filteredCalculationsData.map((calc) => ({
                    id: calc.id,
                    "Key": calc.key_name || `Key ${calc.key_id}`,
                    "Type": calc.calculation_type,
                    "Cost": formatCostColumn(calc),
                    "Surface": formatSurfaceColumn(calc),
                    "Consumption": formatConsumptionColumn(calc),
                    "Variables": formatRemainingVariables(calc.variables),
                    "Result Without VAT": (calc.result_without_vat !== null && calc.result_without_vat !== undefined) || calc.no_cost_destination
                        ? `${formatNumberCH(calc.result_without_vat || 0, { minimumFractionDigits: 0, maximumFractionDigits: 4 })}${calc.result_unit ? ' ' + calc.result_unit : ''}`
                        : '-',
                    "Result With VAT": (calc.result_with_vat !== null && calc.result_with_vat !== undefined) || calc.no_cost_destination
                        ? `${formatNumberCH(calc.result_with_vat || 0, { minimumFractionDigits: 0, maximumFractionDigits: 4 })}${calc.result_unit ? ' ' + calc.result_unit : ''}`
                        : '-',
                    "Building": calc.building_name || 'Unknown Building',
                    "Destination Type": calc.destination_type || '-',
                    "Destination": formatDestinationColumn(calc),
                    // Store raw data for export
                    rawData: calc
                }));

                setFilteredCalculations(formattedCalculations);
                setCalculations(formattedCalculations);
            } else {
                setFilteredCalculations([]);
                setCalculations([]);
                alert("Error fetching calculations: " + res.data.message);
            }
        } catch (err) {
            console.error("Error fetching filtered calculations:", err);
            setFilteredCalculations([]);
            setCalculations([]);
            alert("Error fetching calculations: " + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const getDestinationNumberForExport = (rawData, displayValue) => {
        const type = (rawData?.destination_type || '').toLowerCase();

        // For Buyer destinations, we want the buyer number (e.g. 90210),
        // not the underlying consumption number (e.g. 93).
        if (type === 'buyer') {
            if (rawData?.destination_display) {
                const [numberPart] = rawData.destination_display.split('-');
                if (numberPart && numberPart.trim()) {
                    return numberPart.trim();
                }
                return rawData.destination_display;
            }
            if (rawData?.destination_label) {
                const [numberPart] = rawData.destination_label.split('-');
                if (numberPart && numberPart.trim()) {
                    return numberPart.trim();
                }
                return rawData.destination_label;
            }
            if (typeof displayValue === 'string' && displayValue.length > 0) {
                const [numberPart] = displayValue.split('-');
                return numberPart ? numberPart.trim() : displayValue.trim();
            }
        }

        // Default behaviour for non-buyer destinations:
        // follow the same priority order as the calculations table.
        if (rawData?.destination_number) {
            return rawData.destination_number;
        }
        if (rawData?.destination_id) {
            return rawData.destination_id;
        }
        if (rawData?.destination_display) {
            const [numberPart] = rawData.destination_display.split('-');
            if (numberPart && numberPart.trim()) {
                return numberPart.trim();
            }
            return rawData.destination_display;
        }
        if (rawData?.destination_label) {
            const [numberPart] = rawData.destination_label.split('-');
            if (numberPart && numberPart.trim()) {
                return numberPart.trim();
            }
            return rawData.destination_label;
        }
        if (rawData?.destination_name) {
            return rawData.destination_name;
        }
        if (typeof displayValue === 'string' && displayValue.length > 0) {
            const [numberPart] = displayValue.split('-');
            return numberPart ? numberPart.trim() : displayValue.trim();
        }
        return displayValue || '';
    };

    // Export to Excel functionality
    const exportToExcel = () => {
        // Always use the current calculations state which reflects the applied filters
        // When filters are applied via fetchFilteredCalculations(), the calculations state is updated
        // When no filters are applied, calculations contains all calculations from fetchAllCalculations()
        const calculationsToExport = calculations;

        if (calculationsToExport.length === 0) {
            alert("No data to export.");
            return;
        }

        // Debug: Log what's being exported
        const filterInfo = hasSearched && (selectedBuilding || selectedPeriod) 
            ? `Filtered (Building: ${selectedBuilding?.label || 'N/A'}, Period: ${selectedPeriod?.label || 'N/A'})` 
            : 'All calculations';
        console.log(`Exporting ${calculationsToExport.length} calculations - ${filterInfo}`);

        // Filter out calculations that are transferred to another key
        // Only show final costs (not intermediate transfers)
        const finalCalculations = calculationsToExport.filter(calc => {
            // Exclude calculations where destination_type is 'Key' (transferred to another key)
            // These will appear in the final destination key instead
            return calc["Destination Type"] !== "Key";
        });

        // Prepare data for Excel export with specific columns
        const exportData = finalCalculations.map(calc => {
            const rawData = calc.rawData;
            const destinationNumber = getDestinationNumberForExport(rawData, calc["Destination"]);

            // NOTE for client reconciliation:
            // - "Cost Number" is the pure cost code (e.g. 72-2395), with no text prefix.
            // - "Result With VAT (CHF)" and "Result Without VAT (CHF)" are the amounts to sum.
            return {
                "Key Name": calc.Key || "",
                "Cost Number": rawData?.no_cost_destination ? "-" : (rawData?.cost_number || ""),
                "Surface": rawData?.surface_number || "",
                "Consumption": rawData?.consumption_number || "",
                // Export raw numeric values (no display rounding/formatting)
                "Result Without VAT (CHF)": rawData?.result_without_vat !== null && rawData?.result_without_vat !== undefined && Number.isFinite(Number(rawData.result_without_vat))
                    ? Number(rawData.result_without_vat)
                    : "",
                "Result With VAT (CHF)": rawData?.result_with_vat !== null && rawData?.result_with_vat !== undefined && Number.isFinite(Number(rawData.result_with_vat))
                    ? Number(rawData.result_with_vat)
                    : "",
                "Destination Type": calc["Destination Type"] || "",
                "Destination": destinationNumber || ""
            };
        });

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);

        // Add the worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, "Calculations");

        // Generate filename based on filters or default
        let filename = "All_Calculations.xlsx";
        if (selectedBuilding && selectedPeriod) {
            const buildingName = selectedBuilding.label.replace(/[^a-zA-Z0-9]/g, '_');
            const periodLabel = selectedPeriod.label.replace(/[^a-zA-Z0-9]/g, '_');
            filename = `${buildingName}-${periodLabel}.xlsx`;
        } else if (selectedBuilding) {
            const buildingName = selectedBuilding.label.replace(/[^a-zA-Z0-9]/g, '_');
            filename = `${buildingName}-All_Periods.xlsx`;
        }

        // Save the file
        XLSX.writeFile(wb, filename);
    };

    const formatDestinationColumn = (calc) => {
        // Use the same fallback logic as the calculations popup
        const primaryLabel = 
            calc?.destination_display ||
            calc?.destination_label ||
            calc?.destination_name ||
            calc?.destination_number ||
            calc?.destination_id ||
            "";
        
        return primaryLabel || '-';
    };

    const formatCostColumn = (calc) => {
        // Hide cost information for no_cost_destination calculations
        if (calc.no_cost_destination) {
            return '-';
        }
        let costDisplay = '';
        if (calc.cost_name && calc.cost_number) {
            costDisplay += `${calc.cost_number} - ${calc.cost_name}`;
        }
        if ((calc.cost_value || calc.cost) > 0) {
            const costValue = `${formatNumberCH(calc.cost_value || calc.cost, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CHF`;
            costDisplay += costDisplay ? `: ${costValue}` : costValue;
        }
        return costDisplay || '-';
    };

    const formatSurfaceColumn = (calc) => {
        if (calc.surface_area > 0) {
            // Debug logging for missing surface details
            if (!calc.surface_name || !calc.surface_number) {
                console.log('⚠️ Missing surface details:', {
                    surface_area: calc.surface_area,
                    surface_number: calc.surface_number,
                    surface_name: calc.surface_name,
                    key_name: calc.key_name,
                    calculation: calc
                });
            }
            
            let surfaceDisplay = '';
            if (calc.surface_name && calc.surface_number) {
                surfaceDisplay += `${calc.surface_number} - ${calc.surface_name}`;
            }
            const surfaceValue = `${formatNumberCH(calc.surface_area, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} m²`;
            surfaceDisplay += surfaceDisplay ? `: ${surfaceValue}` : surfaceValue;
            return surfaceDisplay;
        }
        return '-';
    };

    const formatConsumptionColumn = (calc) => {
        if (calc.consumption > 0) {
            let consumptionDisplay = '';
            if (calc.consumption_name && calc.consumption_number) {
                consumptionDisplay += `${calc.consumption_number} - ${calc.consumption_name}`;
            }
            const consumptionValue = `${formatNumberCH(calc.consumption, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kWh`;
            consumptionDisplay += consumptionDisplay ? `: ${consumptionValue}` : consumptionValue;
            return consumptionDisplay;
        }
        return '-';
    };

    const formatRemainingVariables = (variables) => {
        if (!variables || typeof variables !== 'object') return '';
        
        return Object.entries(variables).map(([key, value]) => {
            // Skip surface_area, consumption, and cost as they have their own columns
            if (key === 'surface_area' || key === 'consumption' || key === 'cost') {
                return null;
            }
            
            let displayValue = typeof value === 'number' ? formatNumberCH(value) : value;
            let unit = '';
            
            // Add appropriate units for remaining variable types
            if (key === 'total_surface') {
                unit = ' m²';
            } else if (key === 'total_consumption') {
                unit = ' kWh';
            } else if (key === 'ponderation' || key === 'total_key_ponderation') {
                unit = '';
            }
            
            return `${key}: ${displayValue}${unit}`;
        }).filter(item => item !== null).join(', ');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const columns = ["Key", "Type", "Cost", "Surface", "Consumption", "Variables", "Result Without VAT", "Result With VAT", "Building", "Destination Type", "Destination"];

    return (
        <div className="admin-page calculations-page" style={{ overflowX: 'hidden', maxWidth: '100vw' }}>
            <div className="admin-page-title d-flex align-items-center justify-content-between">
                <h1 className="page-section-title">All Calculations</h1>
            </div>

            {/* Filters Section */}
            <div className="key-card mb-4 p-lg-4 p-3">
                <h5 className="mb-3">Filters</h5>
                <div className="row">
                    <div className="col-md-2">
                        <div className="form-group">
                            <label htmlFor="building">Building</label>
                            <div className="custom-select">
                                <Select
                                    options={availableBuildings}
                                    value={selectedBuilding}
                                    onChange={handleBuildingChange}
                                    placeholder="Select building"
                                    isClearable
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="form-group">
                            <label htmlFor="period">Time Period</label>
                            <div className="custom-select">
                                <Select
                                    options={availablePeriods}
                                    value={selectedPeriod}
                                    onChange={setSelectedPeriod}
                                    placeholder="Select time period (optional)"
                                    isDisabled={!selectedBuilding}
                                    isClearable
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-2">
                        <div className="form-group">
                            <label>&nbsp;</label>
                            <button
                                type="button"
                                className="btn btn-primary w-100"
                                onClick={fetchFilteredCalculations}
                                disabled={!selectedBuilding}
                            >
                                Filter
                            </button>
                        </div>
                    </div>
                    <div className="col-md-2">
                        <div className="form-group">
                            <label>&nbsp;</label>
                            <button
                                type="button"
                                className="btn btn-secondary w-100"
                                onClick={() => {
                                    setSelectedBuilding(null);
                                    setSelectedPeriod(null);
                                    setAvailablePeriods([]);
                                    setHasSearched(false);
                                    fetchAllCalculations();
                                }}
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>&nbsp;</label>
                            <button
                                type="button"
                                className="btn btn-success w-100"
                                onClick={exportToExcel}
                                disabled={!hasSearched || calculations.length === 0}
                                title={!hasSearched 
                                    ? "Please apply filters first before exporting" 
                                    : hasSearched && (selectedBuilding || selectedPeriod) 
                                        ? `Export filtered results (${calculations.length} calculations)` 
                                        : `Export all calculations (${calculations.length} calculations)`}
                            >
                                Export to Excel 
                            </button>
                            {/* {hasSearched && (selectedBuilding || selectedPeriod) ? `(${calculations.length})` : ''} */}
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading calculations...</p>
                </div>
            ) : calculations.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p>No calculations found.</p>
                </div>
            ) : (
                <div className="calculations-table-container" style={{ 
                    overflowX: 'auto', 
                    maxWidth: '100%',
                    border: '1px solid var(--border, #ddd)',
                    borderRadius: '8px'
                }}>
                    <style>
                        {`
                        /* Prevent page-level horizontal scroll */
                        body {
                            overflow-x: hidden !important;
                        }
                        .calculations-page {
                            overflow-x: hidden !important;
                            max-width: 100vw !important;
                            box-sizing: border-box;
                        }
                        
                        /* Table container styles */
                        .calculations-table-container .common-table {
                            border: none;
                            margin: 0;
                        }
                        .calculations-table-container .table-wrpper {
                            overflow: visible;
                        }
                        .calculations-table-container table {
                            min-width: 1700px;
                            width: auto;
                        }
                        .calculations-table-container th,
                        .calculations-table-container td {
                            white-space: nowrap;
                            padding: 12px 8px;
                            min-width: 100px;
                        }
                        .calculations-table-container th:nth-child(1),
                        .calculations-table-container td:nth-child(1) {
                            min-width: 150px; /* Key column */
                        }
                        .calculations-table-container th:nth-child(2),
                        .calculations-table-container td:nth-child(2) {
                            min-width: 120px; /* Type column */
                        }
                        .calculations-table-container th:nth-child(3),
                        .calculations-table-container td:nth-child(3) {
                            min-width: 200px; /* Cost column */
                        }
                        .calculations-table-container th:nth-child(4),
                        .calculations-table-container td:nth-child(4) {
                            min-width: 180px; /* Surface column */
                        }
                        .calculations-table-container th:nth-child(5),
                        .calculations-table-container td:nth-child(5) {
                            min-width: 180px; /* Consumption column */
                        }
                        .calculations-table-container th:nth-child(6),
                        .calculations-table-container td:nth-child(6) {
                            min-width: 200px; /* Variables column */
                            max-width: 300px;
                            white-space: normal;
                            word-wrap: break-word;
                        }
                        .calculations-table-container th:nth-child(7),
                        .calculations-table-container td:nth-child(7) {
                            min-width: 150px; /* Result Without VAT column */
                        }
                        .calculations-table-container th:nth-child(8),
                        .calculations-table-container td:nth-child(8) {
                            min-width: 150px; /* Result With VAT column */
                        }
                        .calculations-table-container th:nth-child(9),
                        .calculations-table-container td:nth-child(9) {
                            min-width: 150px; /* Building column */
                        }
                        .calculations-table-container th:nth-child(10),
                        .calculations-table-container td:nth-child(10) {
                            min-width: 130px; /* Destination Type column */
                        }
                        .calculations-table-container th:nth-child(11),
                        .calculations-table-container td:nth-child(11) {
                            min-width: 200px; /* Destination column */
                        }
                        `}
                    </style>
                    <CommonTable
                        columns={columns}
                        data={calculations}
                        actions={null}
                        itemsPerPage={20}
                    />
                </div>
            )}
        </div>
    );
};

export default Calculations;
